/**
 * Keyed registry of "push this content into that live editor" callbacks.
 *
 * A mounted Froala owns the DOM for one entity id and is the only thing that
 * can `editor.html.set()` it, so imperative callers (insert-into-cell, table
 * paste, the AI agent, design-system propagation, undo/redo) need a way to
 * reach a specific instance. That used to be a SINGLE global —
 * `window.__CONTENT_TRIGGER_UPDATE__` — assigned by every Froala on mount and
 * cleared on unmount. With several editors mounted at once (a table's per-cell
 * instances, most visibly) the last one to mount overwrote every earlier
 * assignment, so a push aimed at any other id hit that instance's guard
 * (`payloadId !== id` → bail) and silently did nothing: a variable inserted
 * into a table cell stayed as raw `{{token}}` text.
 *
 * The registry keys the callback by id, so every mounted editor stays
 * reachable. `window.__CONTENT_TRIGGER_UPDATE__` is kept as a thin
 * compatibility shim over `triggerContentUpdateFor` for the call sites that
 * still reach for the global (they get correct routing for free).
 */

export type ContentTrigger = (id: string, content?: string) => void;

declare global {
  interface Window {
    __CONTENT_TRIGGER_UPDATE__:
      | ((id: string, content?: string) => void)
      | undefined;
  }
}

const triggers = new Map<string, ContentTrigger>();

const globalShim: ContentTrigger = (id, content) => {
  triggerContentUpdateFor(id, content);
};

/** Install (once) the legacy global as a shim over this registry. */
const installGlobalShim = (): void => {
  if (typeof window === "undefined") return;
  if (window.__CONTENT_TRIGGER_UPDATE__ === globalShim) return;
  window.__CONTENT_TRIGGER_UPDATE__ = globalShim;
};

/**
 * Register `fn` as the content-push channel for `id`, replacing any previous
 * entry for that id. Returns a disposer that only removes the entry while it
 * is still the registered one (a remount that re-registers first must not be
 * torn down by the old instance's cleanup).
 */
export const registerContentTrigger = (
  id: string,
  fn: ContentTrigger,
): (() => void) => {
  triggers.set(id, fn);
  installGlobalShim();
  return () => {
    if (triggers.get(id) === fn) triggers.delete(id);
  };
};

/**
 * Push `content` into the editor registered for `id`. Returns true when a
 * registered editor handled it, false when nothing is mounted for that id
 * (the caller's store write is then the only thing that needs to happen).
 */
export const triggerContentUpdateFor = (
  id: string,
  content?: string,
): boolean => {
  const fn = triggers.get(id);
  if (!fn) return false;
  fn(id, content);
  return true;
};

/** Test helper — drop every registration. */
export const resetContentTriggers = (): void => {
  triggers.clear();
};
